/**
 * "Create advertiser page" — one passcode-protected hub for every campaign of
 * a GAM advertiser. The backend links existing campaign pages, creates the
 * missing ones and generates their reports one at a time, detached; unchecked
 * campaigns become `excludeOrderIds`.
 *
 * Related orders under OTHER GAM advertisers (the brand re-booked on a new seat
 * id, an agency booking) are offered as opt-in includes; the backend's 422
 * `cross_advertiser_include` is confirmed by typing the advertiser name, the
 * same rule the Manage modal applies.
 */

import React, { useMemo, useState } from 'react';
import { Button, Modal } from '../../ui';
import type { AdvertiserCandidateGroup, AdvertiserReportPage, CrossAdvertiserOrder } from '../../../services/advertiserReportsApi';
import { createAdvertiserPage, isCrossAdvertiserError } from '../../../services/advertiserReportsApi';
import type { ApiError } from '../../../services/api';
import {
  CREATE_ROW_PLAN_LABEL,
  CreateRowPlan,
  MINUTES_PER_REPORT,
  Notify,
  advertiserPageUrl,
  fieldStyle,
  fmtDate,
  formatLifecycleCounts,
  helpStyle,
  labelStyle,
  lifecycleOf,
  localSlugify,
  pinProblem,
  planCreateRow,
  plural,
  randomPin,
  relatedOrdersForHub,
  slugProblem,
  summaryStripStyle,
} from './shared';
import { LifecyclePill } from './pills';
import CrossAdvertiserConfirm, { crossAdvertiserConfirmed } from './CrossAdvertiserConfirm';

interface CreateAdvertiserPageModalProps {
  group: AdvertiserCandidateGroup;
  /** Every picker group — related orders under other GAM advertisers are found here. */
  groups: AdvertiserCandidateGroup[];
  onClose: () => void;
  onCreated: (result: { page: AdvertiserReportPage; url: string; generateCount: number }) => void;
  notify: Notify;
}

const PLAN_COLOR: Record<CreateRowPlan, string> = {
  link: 'var(--info)',
  linked_elsewhere: 'var(--warning)',
  create: 'var(--success)',
  needs_window: 'var(--warning)',
  in_work: 'var(--fg-4)',
  hidden_upcoming: 'var(--fg-4)',
};

const rowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto minmax(0, 1fr) auto auto',
  alignItems: 'center',
  gap: 10,
  padding: '8px 10px',
  borderTop: '1px solid var(--border-subtle)',
  fontSize: 12.5,
  cursor: 'pointer',
};

const CreateAdvertiserPageModal: React.FC<CreateAdvertiserPageModalProps> = ({ group, groups, onClose, onCreated, notify }) => {
  const [clientLabel, setClientLabel] = useState(group.advertiserName);
  const [slugInput, setSlugInput] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  /** Server-side slug rejection (409 slug_taken) — cleared on the next edit. */
  const [slugError, setSlugError] = useState<string | null>(null);
  const [pin, setPin] = useState<string>(() => randomPin());
  const [checked, setChecked] = useState<Set<string>>(() => new Set(group.orders.map((o) => o.gamOrderId)));
  /** Related orders under other advertisers the admin opted to include (unchecked by default). */
  const [relatedChecked, setRelatedChecked] = useState<Set<string>>(() => new Set());
  const [autoSync, setAutoSync] = useState(true);
  const [showUpcoming, setShowUpcoming] = useState(true);
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<React.ReactNode>(null);
  const [crossConfirm, setCrossConfirm] = useState<{ orders: CrossAdvertiserOrder[]; typed: string } | null>(null);

  // Default slug = normalized GAM company name (the backend does the same when slug is omitted).
  const effectiveSlug = useMemo(
    () => localSlugify(slugTouched ? slugInput : group.advertiserName),
    [slugTouched, slugInput, group.advertiserName],
  );
  const slugIssue = slugError ?? slugProblem(effectiveSlug);
  const pinIssue = pinProblem(pin);

  const rows = useMemo(
    () => group.orders.map((order) => ({ order, plan: planCreateRow(order, showUpcoming) })),
    [group.orders, showUpcoming],
  );
  const countPlan = (plan: CreateRowPlan) => rows.filter((r) => r.plan === plan && checked.has(r.order.gamOrderId)).length;
  const linkCount = countPlan('link');
  const needsWindowCount = countPlan('needs_window');

  /**
   * This brand's orders booked under OTHER GAM advertisers — the case a
   * re-booked "new seat id" order lands in (Dr. Squatch → Unilever, Sept 2026).
   * Terms follow the GAM company name and the label being typed.
   */
  const relatedOrders = useMemo(
    () =>
      relatedOrdersForHub({
        groups,
        labels: [group.advertiserName, clientLabel],
        advertiserIds: [group.advertiserId],
        knownOrderIds: new Set(group.orders.map((o) => o.gamOrderId)),
      }),
    [groups, group, clientLabel],
  );
  const includeOrderIds = useMemo(
    () => relatedOrders.filter((o) => relatedChecked.has(o.gamOrderId) && !o.advertiserPageSlug).map((o) => o.gamOrderId),
    [relatedOrders, relatedChecked],
  );
  const relatedAdvertiserNames = useMemo(
    () => Array.from(new Set(relatedOrders.filter((o) => includeOrderIds.includes(o.gamOrderId)).map((o) => o.advertiserName))),
    [relatedOrders, includeOrderIds],
  );
  const orderNameById = useMemo(() => new Map(relatedOrders.map((o) => [o.gamOrderId, o.gamOrderName])), [relatedOrders]);

  const generateCount =
    countPlan('create') +
    relatedOrders.filter((o) => includeOrderIds.includes(o.gamOrderId) && planCreateRow(o, showUpcoming) === 'create').length;

  const toggle = (id: string) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleRelated = (id: string) =>
    setRelatedChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const canSubmit = !creating && clientLabel.trim() !== '' && !slugIssue && !pinIssue && group.advertiserId !== '';

  const submit = async (confirmCrossAdvertiser = false) => {
    if (creating) return;
    setCreating(true);
    setFormError(null);
    try {
      const excludeOrderIds = group.orders.filter((o) => !checked.has(o.gamOrderId)).map((o) => o.gamOrderId);
      const { page, url } = await createAdvertiserPage({
        gamAdvertiserIds: [group.advertiserId],
        clientLabel: clientLabel.trim(),
        slug: effectiveSlug,
        pin,
        showUpcoming,
        autoSync,
        ...(excludeOrderIds.length > 0 ? { excludeOrderIds } : {}),
        ...(includeOrderIds.length > 0 ? { includeOrderIds } : {}),
        ...(confirmCrossAdvertiser ? { confirmCrossAdvertiser: true } : {}),
      });
      setCrossConfirm(null);
      onCreated({ page, url, generateCount });
    } catch (e: unknown) {
      const err = e as ApiError;
      if (isCrossAdvertiserError(err)) {
        // Another GAM advertiser's orders — confirm by typing its name (shared rule with Manage).
        setCrossConfirm({ orders: err.body.orders, typed: '' });
      } else if (err?.status === 409 && err.code === 'slug_taken') {
        setSlugTouched(true);
        setSlugInput(effectiveSlug);
        setSlugError(err.message);
      } else if (err?.status === 409 && err.code === 'advertiser_has_page') {
        const existingSlug = typeof err.body?.existingSlug === 'string' ? err.body.existingSlug : null;
        setFormError(
          <>
            An advertiser page already covers {group.advertiserName}
            {existingSlug && (
              <>
                {' '}—{' '}
                <a href={advertiserPageUrl(existingSlug)} target="_blank" rel="noreferrer">
                  /advertisers/{existingSlug}/reports
                </a>
              </>
            )}
            . Revoke or delete it first, or refresh the picker to see its actions.
          </>,
        );
      } else {
        setFormError(err?.message || 'Failed to create advertiser page');
        notify('error', 'Failed to create advertiser page', err?.message);
      }
    } finally {
      setCreating(false);
    }
  };

  const crossConfirmed = !!crossConfirm && crossAdvertiserConfirmed(crossConfirm.orders, crossConfirm.typed);

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow="Advertiser pages"
      title="New advertiser page"
      width={680}
      closeOnEsc={false}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          {crossConfirm ? (
            <Button variant="primary" onClick={() => void submit(true)} disabled={creating || !crossConfirmed}>
              {creating ? 'Creating…' : 'Confirm & create'}
            </Button>
          ) : (
            <Button variant="primary" onClick={() => void submit(false)} disabled={!canSubmit}>
              {creating
                ? 'Creating…'
                : generateCount > 0
                  ? `Create advertiser page & generate ${plural(generateCount, 'report')}`
                  : 'Create advertiser page'}
            </Button>
          )}
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={summaryStripStyle}>
          <div style={{ fontWeight: 700 }}>{group.advertiserName}</div>
          <div style={{ color: 'var(--fg-3)', fontSize: 12.5, marginTop: 2 }}>
            GAM advertiser {group.advertiserId || 'unresolved'} · {plural(group.orders.length, 'order')} found ·{' '}
            {formatLifecycleCounts(group.counts)}
          </div>
        </div>

        {formError && (
          <div style={{ background: 'var(--danger-soft)', color: 'var(--danger)', borderRadius: 'var(--radius-md)', padding: '9px 12px', fontSize: 12.5 }}>
            {formError}
          </div>
        )}

        {crossConfirm && (
          <CrossAdvertiserConfirm
            orders={crossConfirm.orders}
            clientLabel={clientLabel.trim() || group.advertiserName}
            typed={crossConfirm.typed}
            onTyped={(typed) => setCrossConfirm({ ...crossConfirm, typed })}
            onBack={() => setCrossConfirm(null)}
            orderNameById={orderNameById}
          />
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle}>Client label *</label>
            <input style={fieldStyle} value={clientLabel} onChange={(e) => setClientLabel(e.target.value)} placeholder="The Coca-Cola Company" />
            <div style={helpStyle}>Shown as the page title. Defaults to the GAM company name.</div>
          </div>
          <div>
            <label style={labelStyle}>Passcode (4 digits) *</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                style={{ ...fieldStyle, letterSpacing: 4, fontFamily: 'monospace', width: 90 }}
                value={pin}
                maxLength={4}
                inputMode="numeric"
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              />
              <Button size="sm" variant="ghost" onClick={() => setPin(randomPin())}>🎲 New</Button>
            </div>
            <div style={{ ...helpStyle, color: pinIssue ? 'var(--danger)' : helpStyle.color }}>
              {pinIssue ?? 'One passcode opens this page and every campaign report on it.'}
            </div>
          </div>
        </div>

        <div>
          <label style={labelStyle}>Page URL</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, color: 'var(--fg-4)', whiteSpace: 'nowrap' }}>culturegenesis.com/advertisers/</span>
            <input
              style={{ ...fieldStyle, width: 260 }}
              value={slugTouched ? slugInput : effectiveSlug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlugInput(e.target.value);
                setSlugError(null);
              }}
              placeholder="the-coca-cola-company"
            />
            <span style={{ fontSize: 12.5, color: 'var(--fg-4)' }}>/reports</span>
          </div>
          <div style={{ ...helpStyle, color: slugIssue ? 'var(--danger)' : helpStyle.color }}>
            {slugIssue ?? 'Permanent — the slug can’t be changed after creation.'}
          </div>
        </div>

        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>
              Campaigns to include ({checked.size}/{group.orders.length})
            </label>
            <button type="button" style={linkButtonStyle} onClick={() => setChecked(new Set(group.orders.map((o) => o.gamOrderId)))}>
              Select all
            </button>
            <button type="button" style={linkButtonStyle} onClick={() => setChecked(new Set())}>
              None
            </button>
          </div>
          <div style={{ border: '1px solid var(--border-1)', borderRadius: 'var(--radius-md)', maxHeight: 280, overflowY: 'auto', marginTop: 6 }}>
            {rows.map(({ order, plan }, i) => {
              const isChecked = checked.has(order.gamOrderId);
              return (
                <label
                  key={order.gamOrderId}
                  style={{ ...rowStyle, borderTop: i === 0 ? 'none' : rowStyle.borderTop, opacity: isChecked ? 1 : 0.6 }}
                >
                  <input type="checkbox" checked={isChecked} onChange={() => toggle(order.gamOrderId)} />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ fontWeight: 600, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {order.gamOrderName}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--fg-4)' }}>
                      Order {order.gamOrderId} · {fmtDate(order.startDate)} → {order.endDate ? fmtDate(order.endDate) : 'no end date'}
                    </span>
                  </span>
                  <LifecyclePill lifecycle={lifecycleOf(order)} />
                  <span style={{ fontSize: 11.5, color: PLAN_COLOR[plan], whiteSpace: 'nowrap' }}>{CREATE_ROW_PLAN_LABEL[plan]}</span>
                </label>
              );
            })}
          </div>
          <div style={helpStyle}>
            Unchecked campaigns are hidden from the client (you can add them back from Manage).{' '}
            {linkCount > 0 && <>{plural(linkCount, 'existing page')} will be linked. </>}
            {needsWindowCount > 0 && (
              <>
                {plural(needsWindowCount, 'campaign')} without an end date{' '}
                {needsWindowCount === 1 ? 'needs' : 'need'} a reporting window — create{' '}
                {needsWindowCount === 1 ? 'its page' : 'those pages'} from Manage after this.
              </>
            )}
          </div>
        </div>

        {relatedOrders.length > 0 && (
          <div>
            <label style={labelStyle}>Related orders under other GAM advertisers ({relatedOrders.length})</label>
            <div style={{ border: '1px solid var(--border-1)', borderRadius: 'var(--radius-md)', maxHeight: 220, overflowY: 'auto', marginTop: 6 }}>
              {relatedOrders.map((order, i) => {
                const linkedElsewhere = order.advertiserPageSlug;
                const isChecked = !linkedElsewhere && relatedChecked.has(order.gamOrderId);
                return (
                  <label
                    key={order.gamOrderId}
                    style={{
                      ...rowStyle,
                      borderTop: i === 0 ? 'none' : rowStyle.borderTop,
                      opacity: isChecked || linkedElsewhere ? 1 : 0.75,
                      cursor: linkedElsewhere ? 'default' : 'pointer',
                    }}
                  >
                    <input type="checkbox" checked={isChecked} disabled={!!linkedElsewhere} onChange={() => toggleRelated(order.gamOrderId)} />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ fontWeight: 600, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {order.gamOrderName}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--fg-4)' }}>
                        Order {order.gamOrderId} · {fmtDate(order.startDate)} → {order.endDate ? fmtDate(order.endDate) : 'no end date'} · booked under{' '}
                        <strong style={{ color: 'var(--warning)' }}>{order.advertiserName}</strong>
                        {(order.totalImpressionsDelivered ?? 0) > 0 && <> · {(order.totalImpressionsDelivered ?? 0).toLocaleString()} impressions</>}
                        {linkedElsewhere && <> · its page is linked to /advertisers/{linkedElsewhere}/reports</>}
                      </span>
                    </span>
                    <LifecyclePill lifecycle={lifecycleOf(order)} />
                    <span style={{ fontSize: 11.5, color: isChecked ? 'var(--warning)' : 'var(--fg-4)', whiteSpace: 'nowrap' }}>
                      {linkedElsewhere ? 'linked elsewhere' : isChecked ? 'include' : 'not included'}
                    </span>
                  </label>
                );
              })}
            </div>
            <div style={{ ...helpStyle, color: includeOrderIds.length > 0 ? 'var(--warning)' : helpStyle.color }}>
              {includeOrderIds.length > 0 ? (
                <>
                  The <strong>{clientLabel.trim() || group.advertiserName}</strong> passcode will also open{' '}
                  {plural(includeOrderIds.length, 'report')} booked under {relatedAdvertiserNames.join(', ')}, spend included — you’ll
                  confirm by typing the advertiser name.
                </>
              ) : (
                <>
                  Same brand name, different GAM advertiser — a campaign re-booked on a new seat id, or an agency booking. Unchecked
                  by default; tick to include on this page (you can also do this later from Manage).
                </>
              )}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13 }}>
            <input type="checkbox" checked={autoSync} onChange={(e) => setAutoSync(e.target.checked)} />
            Auto-add new campaigns for this advertiser (checked hourly)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13 }}>
            <input type="checkbox" checked={showUpcoming} onChange={(e) => setShowUpcoming(e.target.checked)} />
            Show upcoming campaigns to the client
          </label>
        </div>

        <div style={{ fontSize: 12.5, color: 'var(--fg-3)' }}>
          {generateCount > 0 ? (
            <>
              {plural(generateCount, 'campaign report')} will generate one at a time (~{MINUTES_PER_REPORT} min each) right after
              creation — the hub is live immediately and fills in as reports finish.
            </>
          ) : (
            <>No new reports to generate — existing campaign pages are linked to the hub on creation.</>
          )}
        </div>
      </div>
    </Modal>
  );
};

const linkButtonStyle: React.CSSProperties = {
  border: 'none',
  background: 'none',
  padding: 0,
  cursor: 'pointer',
  fontSize: 11.5,
  color: 'var(--fg-link)',
  fontFamily: 'inherit',
};

export default CreateAdvertiserPageModal;
